import axios from 'axios';
import { useAuthStore } from '../../auth/useAuthStore';
import { performanceProfiler } from './performance-profiler.service';
import type {
  CanonicalNode,
  CanonicalEdge,
  CreateCanonicalNodeDto,
  CreateCanonicalEdgeDto,
  UpdateCanonicalNodeDto,
  UpdateCanonicalEdgeDto,
  QueryNetworkParams,
  NetworkStats,
  MergeEdgesResponse,
  UnmergeEdgeResponse,
  MergeSnapshot,
  CreateBranchDto,
  CreateBranchResult,
  ChainValidationResult,
  MergeMultipleEdgesResponse,
  UnmergeMultipleEdgesResponse,
  MultiMergeSnapshot,
  EdgeSemanticInfo,
} from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';
const edgeSemanticCache = new Map<string, EdgeSemanticInfo | null>();
const edgeSemanticPending = new Map<string, Promise<EdgeSemanticInfo | null>>();

// Log API_URL once on module initialization for diagnostics
console.log('[canonical-network-api] API_URL:', API_URL);

/**
 * Obtiene el token JWT activo.
 *
 * @remarks
 * Toma primero el token en memoria del auth store. Si no está disponible,
 * usa como fallback la persistencia de Zustand en localStorage.
 */
const getAuthToken = (): string | null => {
  const storeToken = useAuthStore.getState().token;
  if (storeToken) {
    return storeToken;
  }

  try {
    const authStorage = localStorage.getItem('auth-storage');
    if (!authStorage) return null;

    const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
    return parsed.state?.token || null;
  } catch (error) {
    console.warn('[canonical-network-api] Error parsing auth-storage:', error);
    return null;
  }
};

/**
 * Construye headers autenticados para requests al backend.
 */
const getAuthHeaders = (): Record<string, string> => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildSemanticCacheKey = (projectId: string, edgeId: string): string =>
  `${projectId}:${edgeId}`;

export interface RepairTargetSuggestion {
  type: 'edge' | 'node';
  id: string;
  distanceToNetwork: number | null;
  positionOnEdge: number | null;
  projectedCoordinate: [number, number] | null;
  sideOfEdge: string | null;
  projectionType: string;
  confidenceScore: number;
  withinThreshold: boolean;
  status: string;
}

export interface DeletionImpactAssociationPreview {
  associationId: string;
  entityType: string;
  entityId: string;
  currentTarget: {
    type: string;
    id: string | null;
  };
  method: 'automatic' | 'manual';
  status: string;
  isPrimary: boolean;
  isLocked: boolean;
  autoRepairable: boolean;
  requiresManualResolution: boolean;
  reason: string;
  suggestedTarget: RepairTargetSuggestion | null;
}

export interface DeletionImpact {
  totalAffected: number;
  willBeDeleted: number;
  willBeOrphaned: number;
  willBlockDeletion: number;
  automaticAssociations: number;
  manualAssociations: number;
  lockedAssociations: number;
  repairableAutomatically: number;
  requiresManualResolution: number;
  canDelete: boolean;
  canDeleteDirectly: boolean;
  canDeleteWithAutoRepair: boolean;
  byEntityType: Record<string, { count: number; label: string }>;
  associations: DeletionImpactAssociationPreview[];
}

/**
 * Cliente API para red vial canónica (Canonical Street Network)
 *
 * @remarks
 * Proporciona métodos para interactuar con nodes y edges de la red canónica.
 * Todos los métodos incluyen automáticamente el token JWT si está disponible.
 *
 * @example
 * ```typescript
 * import { canonicalNetworkApi } from '@/core/spatial';
 *
 * // Crear un node
 * const node = await canonicalNetworkApi.createNode(projectId, {
 *   geometry: { type: 'Point', coordinates: [-68.8458, -32.8895] },
 *   sourceType: 'manual'
 * });
 *
 * // Crear un edge
 * const edge = await canonicalNetworkApi.createEdge(projectId, {
 *   fromNodeId: node1.id,
 *   toNodeId: node2.id,
 *   geometry: linestring,
 *   roadClass: 'residential'
 * });
 *
 * // Consultar edges en área
 * const edges = await canonicalNetworkApi.queryEdges(projectId, {
 *   bounds: [-68.85, -32.89, -68.84, -32.88]
 * });
 * ```
 */
export const canonicalNetworkApi = {
  // ==================== NODE OPERATIONS ====================

  /**
   * Crear un nuevo node
   *
   * @param projectId - UUID del proyecto
   * @param data - Datos del node
   * @returns Node creado
   */
  async createNode(
    projectId: string,
    data: CreateCanonicalNodeDto
  ): Promise<CanonicalNode> {
    // PERF: Instrumentación para profiling
    const session = performanceProfiler.getCurrentSession();
    const sessionId = session?.sessionId || 'no-session';

    performanceProfiler.markStageStart(sessionId, 'api-createNode-request', {
      projectId,
      coords: data.geometry.coordinates,
    });

    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/network/nodes`;
    console.log('[canonicalNetworkApi] 📤 POST createNode (fetch):', { url, data, hasToken: !!token });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });

      performanceProfiler.markStageEnd(sessionId, 'api-createNode-request', {
        status: response.status,
        ok: response.ok,
      });

      performanceProfiler.markStageStart(sessionId, 'api-createNode-parse');

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[canonicalNetworkApi] ❌ createNode error:', {
          status: response.status,
          data: errorData,
        });
        performanceProfiler.markStageEnd(sessionId, 'api-createNode-parse');
        throw new Error(`HTTP ${response.status}: ${JSON.stringify(errorData)}`);
      }

      const result = await response.json();
      performanceProfiler.markStageEnd(sessionId, 'api-createNode-parse', {
        nodeId: result.id,
      });

      console.log('[canonicalNetworkApi] ✅ createNode response:', result);
      return result;
    } catch (error: any) {
      console.error('[canonicalNetworkApi] ❌ createNode error:', {
        message: error.message,
      });
      throw error;
    }
  },

  /**
   * Consultar nodes en un proyecto
   *
   * @param projectId - UUID del proyecto
   * @param params - Parámetros de consulta (bounds, status, etc.)
   * @returns Array de nodes
   */
  async queryNodes(
    projectId: string,
    params: QueryNetworkParams = {}
  ): Promise<CanonicalNode[]> {
    const response = await axios.get<CanonicalNode[]>(
      `${API_URL}/projects/${projectId}/network/nodes`,
      {
        headers: getAuthHeaders(),
        params,
      }
    );
    return response.data;
  },

  /**
   * Obtener un node específico
   *
   * @param nodeId - UUID del node
   * @returns Node con relaciones
   */
  async getNode(nodeId: string): Promise<CanonicalNode> {
    const response = await axios.get<CanonicalNode>(
      `${API_URL}/network/nodes/${nodeId}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Actualizar un node (PATCH)
   *
   * @remarks
   * Actualización parcial - solo los campos proporcionados se actualizarán.
   * Comúnmente usado para mover nodes (actualizar geometry).
   *
   * @param nodeId - UUID del node
   * @param data - Campos a actualizar
   * @returns Node actualizado
   */
  async updateNode(
    nodeId: string,
    data: UpdateCanonicalNodeDto
  ): Promise<CanonicalNode> {
    const response = await axios.patch<CanonicalNode>(
      `${API_URL}/network/nodes/${nodeId}`,
      data,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Eliminar un node
   *
   * @remarks Falla si el node tiene edges conectados
   * @param nodeId - UUID del node
   */
  async deleteNode(
    nodeId: string,
    options?: { repairStrategy?: 'auto' },
  ): Promise<void> {
    await axios.delete(`${API_URL}/network/nodes/${nodeId}`, {
      headers: getAuthHeaders(),
      params: options?.repairStrategy ? { repair: options.repairStrategy } : undefined,
    });
  },

  // ==================== EDGE OPERATIONS ====================

  /**
   * Crear un nuevo edge
   *
   * @param projectId - UUID del proyecto
   * @param data - Datos del edge
   * @returns Edge creado
   */
  async createEdge(
    projectId: string,
    data: CreateCanonicalEdgeDto
  ): Promise<CanonicalEdge> {
    // PERF: Instrumentación para profiling
    const session = performanceProfiler.getCurrentSession();
    const sessionId = session?.sessionId || 'no-session';

    performanceProfiler.markStageStart(sessionId, 'api-createEdge-request', {
      projectId,
      fromNodeId: data.fromNodeId,
      toNodeId: data.toNodeId,
      roadClass: data.roadClass,
    });

    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/network/edges`;
    console.log('[canonicalNetworkApi] 📤 POST createEdge (fetch):', { url, hasToken: !!token });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });

      performanceProfiler.markStageEnd(sessionId, 'api-createEdge-request', {
        status: response.status,
        ok: response.ok,
      });

      performanceProfiler.markStageStart(sessionId, 'api-createEdge-parse');

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[canonicalNetworkApi] ❌ createEdge error:', {
          status: response.status,
          data: errorData,
        });
        performanceProfiler.markStageEnd(sessionId, 'api-createEdge-parse');
        throw new Error(`HTTP ${response.status}: ${JSON.stringify(errorData)}`);
      }

      const result = await response.json();
      performanceProfiler.markStageEnd(sessionId, 'api-createEdge-parse', {
        edgeId: result.id,
      });

      console.log('[canonicalNetworkApi] ✅ createEdge response:', result);
      return result;
    } catch (error: any) {
      console.error('[canonicalNetworkApi] ❌ createEdge error:', {
        message: error.message,
      });
      throw error;
    }
  },

  /**
   * Crear rama desde nodo existente (unified endpoint)
   *
   * @remarks
   * Endpoint unificado que maneja atómicamente:
   * - CASO 1: snap-node - Conectar a nodo existente (solo crea edge)
   * - CASO 2: free-point - Crear nuevo nodo + edge
   * - CASO 3: snap-edge - NOT IMPLEMENTED (retorna 400)
   *
   * Ventajas vs flujo secuencial:
   * - Atomicidad real (rollback automático si falla cualquier operación)
   * - Reducción de round-trips HTTP (2 requests → 1 request)
   * - ~45-90ms más rápido (13-19% mejora)
   * - undoSnapshot incluido para undo/redo
   *
   * @param projectId - UUID del proyecto
   * @param data - DTO con intent type y datos de node/edge
   * @returns Nodo target (creado o existente), edges creados, y snapshot para undo
   *
   * @example
   * ```typescript
   * // CASO 1: snap-node (conectar a nodo existente)
   * const result = await canonicalNetworkApi.createBranch(projectId, {
   *   intentType: 'snap-node',
   *   sourceNodeId: 'node-A',
   *   targetNodeId: 'node-B',
   *   edge: {
   *     geometry: linestring,
   *     roadClass: 'residential',
   *   },
   * });
   *
   * // CASO 2: free-point (crear nuevo nodo)
   * const result = await canonicalNetworkApi.createBranch(projectId, {
   *   intentType: 'free-point',
   *   sourceNodeId: 'node-A',
   *   newNode: {
   *     geometry: { type: 'Point', coordinates: [-68.84, -32.89] },
   *     sourceType: 'manual',
   *   },
   *   edge: {
   *     geometry: linestring,
   *     roadClass: 'residential',
   *   },
   * });
   * ```
   */
  async createBranch(
    projectId: string,
    data: CreateBranchDto
  ): Promise<CreateBranchResult> {
    // PERF: Instrumentación para profiling
    const session = performanceProfiler.getCurrentSession();
    const sessionId = session?.sessionId || 'no-session';

    performanceProfiler.markStageStart(sessionId, 'api-createBranch-request', {
      projectId,
      intentType: data.intentType,
      sourceNodeId: data.sourceNodeId,
      targetNodeId: data.targetNodeId,
      hasNewNode: !!data.newNode,
    });

    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/network/create-branch`;
    console.log('[canonicalNetworkApi] 📤 POST createBranch (fetch):', {
      url,
      intentType: data.intentType,
      sourceNodeId: data.sourceNodeId,
      hasToken: !!token,
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });

      performanceProfiler.markStageEnd(sessionId, 'api-createBranch-request', {
        status: response.status,
        ok: response.ok,
      });

      performanceProfiler.markStageStart(sessionId, 'api-createBranch-parse');

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[canonicalNetworkApi] ❌ createBranch error:', {
          status: response.status,
          data: errorData,
        });
        performanceProfiler.markStageEnd(sessionId, 'api-createBranch-parse');
        throw new Error(`HTTP ${response.status}: ${JSON.stringify(errorData)}`);
      }

      const result = await response.json();
      performanceProfiler.markStageEnd(sessionId, 'api-createBranch-parse', {
        nodeId: result.node?.id,
        edgesCount: result.edges?.length,
        createdNodeId: result.undoSnapshot?.createdNodeId,
      });

      console.log('[canonicalNetworkApi] ✅ createBranch response:', {
        nodeId: result.node?.id,
        edgesCount: result.edges?.length,
        undoSnapshot: result.undoSnapshot,
      });
      return result;
    } catch (error: any) {
      console.error('[canonicalNetworkApi] ❌ createBranch error:', {
        message: error.message,
      });
      throw error;
    }
  },

  /**
   * Consultar edges en un proyecto
   *
   * @param projectId - UUID del proyecto
   * @param params - Parámetros de consulta (bounds, status, etc.)
   * @returns Array de edges (incluye fromNode y toNode)
   */
  async queryEdges(
    projectId: string,
    params: QueryNetworkParams = {}
  ): Promise<CanonicalEdge[]> {
    const response = await axios.get<CanonicalEdge[]>(
      `${API_URL}/projects/${projectId}/network/edges`,
      {
        headers: getAuthHeaders(),
        params,
      }
    );
    return response.data;
  },

  /**
   * Obtener un edge específico
   *
   * @param edgeId - UUID del edge
   * @returns Edge con relaciones completas
   */
  async getEdge(edgeId: string): Promise<CanonicalEdge> {
    const response = await axios.get<CanonicalEdge>(
      `${API_URL}/network/edges/${edgeId}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Obtiene la semántica persistida de un segmento y la cachea por sesión.
   *
   * @remarks
   * Retorna `null` si el backend responde 404 para permitir fallback discreto.
   * No debe usarse desde hover: para hover usar `peekCachedEdgeSemanticName`.
   */
  async getEdgeSemanticName(
    projectId: string,
    edgeId: string,
    options: { forceRefresh?: boolean } = {},
  ): Promise<EdgeSemanticInfo | null> {
    const cacheKey = buildSemanticCacheKey(projectId, edgeId);

    if (!options.forceRefresh && edgeSemanticCache.has(cacheKey)) {
      return edgeSemanticCache.get(cacheKey) ?? null;
    }

    const pending = edgeSemanticPending.get(cacheKey);
    if (!options.forceRefresh && pending) {
      return pending;
    }

    const request = axios
      .get<EdgeSemanticInfo>(
        `${API_URL}/projects/${projectId}/segments/${edgeId}/semantic-name`,
        {
          headers: getAuthHeaders(),
        },
      )
      .then((response) => response.data)
      .catch((error: unknown) => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          return null;
        }
        throw error;
      })
      .then((semantic) => {
        edgeSemanticCache.set(cacheKey, semantic);
        edgeSemanticPending.delete(cacheKey);
        return semantic;
      })
      .catch((error) => {
        edgeSemanticPending.delete(cacheKey);
        throw error;
      });

    edgeSemanticPending.set(cacheKey, request);
    return request;
  },

  /**
   * Lee semántica desde caché local, sin disparar requests.
   */
  peekCachedEdgeSemanticName(projectId: string, edgeId: string): EdgeSemanticInfo | null | undefined {
    return edgeSemanticCache.get(buildSemanticCacheKey(projectId, edgeId));
  },

  /**
   * Actualizar un edge (PATCH)
   *
   * @remarks
   * Actualización parcial - solo los campos proporcionados se actualizarán.
   * Si la geometry se actualiza, la longitud se recalculará automáticamente
   * a menos que se proporcione explícitamente.
   * Comúnmente usado para actualizar geometry cuando se mueven nodes.
   *
   * @param edgeId - UUID del edge
   * @param data - Campos a actualizar
   * @returns Edge actualizado
   */
  async updateEdge(
    edgeId: string,
    data: UpdateCanonicalEdgeDto
  ): Promise<CanonicalEdge> {
    const response = await axios.patch<CanonicalEdge>(
      `${API_URL}/network/edges/${edgeId}`,
      data,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Eliminar un edge
   *
   * @param projectId - UUID del proyecto (requerido para consistencia con otras APIs)
   * @param edgeId - UUID del edge
   */
  async deleteEdge(
    _projectId: string,
    edgeId: string,
    options?: { repairStrategy?: 'auto' },
  ): Promise<void> {
    // NOTA: El endpoint actual no requiere projectId, pero lo mantenemos en firma
    // para consistencia con createEdge y otras APIs que sí lo requieren
    await axios.delete(`${API_URL}/network/edges/${edgeId}`, {
      headers: getAuthHeaders(),
      params: options?.repairStrategy ? { repair: options.repairStrategy } : undefined,
    });
  },

  // ==================== IMPACT VERIFICATION ====================

  /**
   * Obtener impacto de eliminar un edge
   *
   * @remarks
   * Llamar antes de eliminar para mostrar al usuario que entidades serán afectadas.
   * Retorna conteo de asociaciones que se eliminarán vs quedarán huérfanas.
   *
   * @param edgeId - UUID del edge
   * @returns Resumen de impacto con conteos por tipo de entidad
   */
  async getEdgeDeletionImpact(edgeId: string): Promise<DeletionImpact> {
    const response = await axios.get<DeletionImpact>(
      `${API_URL}/network/edges/${edgeId}/impact`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Obtener impacto de eliminar un node
   *
   * @remarks
   * Llamar antes de eliminar para mostrar al usuario que entidades serán afectadas.
   * Nota: Eliminar un node también falla si tiene edges conectados.
   *
   * @param nodeId - UUID del node
   * @returns Resumen de impacto con conteos por tipo de entidad
   */
  async getNodeDeletionImpact(nodeId: string): Promise<DeletionImpact> {
    const response = await axios.get<DeletionImpact>(
      `${API_URL}/network/nodes/${nodeId}/impact`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  },

  // ==================== STATISTICS & UTILITIES ====================

  /**
   * Obtener estadísticas de la red
   *
   * @param projectId - UUID del proyecto
   * @returns Estadísticas (nodes, edges, longitud total, distribución)
   */
  async getNetworkStats(projectId: string): Promise<NetworkStats> {
    const response = await axios.get<NetworkStats>(
      `${API_URL}/projects/${projectId}/network/stats`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Obtener estadísticas de deduplicación
   *
   * @param projectId - UUID del proyecto
   * @returns Estadísticas sobre fuentes y deduplicación
   */
  async getDeduplicationStats(projectId: string): Promise<{
    totalEdges: number;
    bySourceType: Record<string, number>;
    withSourceReference: number;
    manualAuthority: number;
  }> {
    const response = await axios.get(
      `${API_URL}/projects/${projectId}/network/deduplication-stats`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  },

  // ==================== NETWORK ACTIVATION ====================

  /**
   * Activa la red canónica convirtiendo vías base en nodos + edges
   *
   * @remarks
   * Este método ejecuta el proceso completo de conversión:
   * 1. Lee vías base del proyecto (BaseRoadNetwork)
   * 2. Detecta intersecciones entre vías
   * 3. Crea nodos en intersecciones y extremos
   * 4. Segmenta vías en edges individuales
   * 5. Guarda todo en transacción
   *
   * El proceso puede tardar varios segundos dependiendo de la cantidad de vías.
   *
   * @param projectId - UUID del proyecto
   * @returns Resultado con estadísticas de conversión
   *
   * @example
   * ```typescript
   * const result = await canonicalNetworkApi.activateNetwork(projectId);
   * console.log(`Red activada: ${result.nodesCreated} nodos, ${result.edgesCreated} edges`);
   * console.log(`Tiempo: ${result.duration}ms`);
   * ```
   */
  async activateNetwork(projectId: string): Promise<{
    nodesCreated: number;
    edgesCreated: number;
    totalLengthKm: number;
    byRoadClass: Record<string, number>;
    duration: number;
  }> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/network/activate`;
    console.log('[canonicalNetworkApi] 📤 POST activateNetwork:', { url, hasToken: !!token });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[canonicalNetworkApi] ❌ activateNetwork error:', {
          status: response.status,
          data: errorData,
        });
        throw new Error(`HTTP ${response.status}: ${JSON.stringify(errorData)}`);
      }

      const result = await response.json();
      console.log('[canonicalNetworkApi] ✅ activateNetwork response:', result);
      return result;
    } catch (error: any) {
      console.error('[canonicalNetworkApi] ❌ activateNetwork error:', {
        message: error.message,
      });
      throw error;
    }
  },

  // ==================== BATCH OPERATIONS ====================

  /**
   * Mover un nodo y sus edges conectados (operación batch atómica)
   *
   * FASE 3: Este método reemplaza updateNode() individual con batch endpoint
   *
   * @param projectId - UUID del proyecto
   * @param data - Payload batch con nodo y edges
   * @returns Nodo y edges actualizados
   *
   * @example
   * ```typescript
   * const result = await canonicalNetworkApi.batchMoveNode(projectId, {
   *   nodeId: 'node-123',
   *   nodeGeometry: { type: 'Point', coordinates: [-68.8459, -32.8896] },
   *   expectedNodeVersion: 1,
   *   edgeUpdates: [
   *     {
   *       edgeId: 'edge-456',
   *       geometry: { type: 'LineString', coordinates: [[...], [...]] },
   *       expectedEdgeVersion: 1,
   *     },
   *   ],
   * });
   * ```
   */
  async batchMoveNode(
    projectId: string,
    data: {
      nodeId: string;
      nodeGeometry: { type: 'Point'; coordinates: [number, number] };
      expectedNodeVersion: number;
      edgeUpdates: Array<{
        edgeId: string;
        geometry: { type: 'LineString'; coordinates: number[][] };
        expectedEdgeVersion: number;
      }>;
    }
  ): Promise<{
    node: CanonicalNode;
    edges: CanonicalEdge[];
  }> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/network/batch/move-node`;
    console.log('[canonicalNetworkApi] 📤 POST batchMoveNode:', {
      url,
      nodeId: data.nodeId,
      edgesCount: data.edgeUpdates.length,
      hasToken: !!token,
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[canonicalNetworkApi] ❌ batchMoveNode error:', {
          status: response.status,
          data: errorData,
        });

        // Lanzar error con información completa para manejo específico
        const error = new Error(`HTTP ${response.status}`) as any;
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      const result = await response.json();
      console.log('[canonicalNetworkApi] ✅ batchMoveNode response:', {
        nodeId: result.node?.id,
        edgesCount: result.edges?.length,
      });
      return result;
    } catch (error: any) {
      console.error('[canonicalNetworkApi] ❌ batchMoveNode error:', {
        message: error.message,
        status: error.status,
      });
      throw error;
    }
  },

  // ==================== NETWORK ANALYSIS UPDATE ====================

  /**
   * Disparar actualización de métricas de sintaxis espacial
   *
   * NOTA: Los cambios de red YA están persistidos en la DB.
   * Este método solo incrementa la versión de red y dispara el recálculo de sintaxis.
   *
   * @param projectId - UUID del proyecto
   * @returns Network version actualizada y mensaje
   */
  async commitNetworkChanges(
    projectId: string
  ): Promise<{ networkVersion: number; message: string }> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/network/commit`;
    console.log('[canonicalNetworkApi] 📤 POST commitNetworkChanges:', { url, hasToken: !!token });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[canonicalNetworkApi] ❌ commitNetworkChanges error:', {
          status: response.status,
          data: errorData,
        });

        const error = new Error(`HTTP ${response.status}`) as any;
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      const result = await response.json();
      console.log('[canonicalNetworkApi] ✅ commitNetworkChanges response:', {
        networkVersion: result.networkVersion,
        message: result.message,
      });
      return result;
    } catch (error: any) {
      console.error('[canonicalNetworkApi] ❌ commitNetworkChanges error:', {
        message: error.message,
        status: error.status,
      });
      throw error;
    }
  },

  // ==================== EXPORT ====================

  /**
   * Exportar la red canónica como ZIP con dos GeoJSON separados.
   *
   * @remarks
   * El ZIP contiene:
   *   - urban_network_segments.geojson (LineString)
   *   - urban_network_nodes.geojson    (Point)
   *
   * Shape points quedan embebidos en la geometría LineString de cada segmento
   * y NO se exportan como capa independiente.
   *
   * @param projectId - UUID del proyecto
   * @returns Blob ZIP para descarga directa
   * @throws Error si el proyecto no existe, no tiene red urbana o el usuario no tiene acceso
   */
  async exportNetwork(projectId: string): Promise<Blob> {
    const response = await axios.get<Blob>(
      `${API_URL}/projects/${projectId}/network/export`,
      {
        headers: getAuthHeaders(),
        responseType: 'blob',
      }
    );
    return response.data;
  },

  // ==================== EDGE MERGE/UNMERGE ====================

  /**
   * Merge two contiguous edges into a single edge
   *
   * @remarks
   * Merges two edges that share a single intermediate node with degree=2.
   * The shared node becomes an internal shape point in the merged geometry.
   *
   * Requirements:
   * - Edges must share exactly one node
   * - Shared node must have degree = 2 (connects only these two edges)
   * - Edges must have matching critical attributes (roadClass, oneway, layer)
   * - For oneway edges, must have sequential directional flow
   * - No blocking external references (e.g., WasteRouteSegment)
   *
   * @param projectId - UUID del proyecto
   * @param edgeId1 - ID of first edge to merge
   * @param edgeId2 - ID of second edge to merge
   * @returns Merge result with merged edge and snapshot for undo
   *
   * @example
   * ```typescript
   * const result = await canonicalNetworkApi.mergeEdges(projectId, edge1.id, edge2.id);
   * console.log(`Merged edge created: ${result.mergedEdge.id}`);
   * console.log(`Deleted edges: ${result.deletedEdgeIds.join(', ')}`);
   * // Save snapshot for undo
   * const snapshot = result.snapshot;
   * ```
   */
  async mergeEdges(
    projectId: string,
    edgeId1: string,
    edgeId2: string
  ): Promise<MergeEdgesResponse> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/network/edges/merge`;
    console.log('[canonicalNetworkApi] 📤 POST mergeEdges (fetch):', {
      url,
      edgeId1,
      edgeId2,
      hasToken: !!token,
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ edgeId1, edgeId2 }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[canonicalNetworkApi] ❌ mergeEdges error:', {
          status: response.status,
          data: errorData,
        });

        const error = new Error(`HTTP ${response.status}`) as any;
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      const result = await response.json();
      console.log('[canonicalNetworkApi] ✅ mergeEdges response:', {
        mergedEdgeId: result.mergedEdge.id,
        deletedEdgeIds: result.deletedEdgeIds,
      });
      return result;
    } catch (error: any) {
      console.error('[canonicalNetworkApi] ❌ mergeEdges error:', {
        message: error.message,
        status: error.status,
      });
      throw error;
    }
  },

  /**
   * Undo a merge operation (unmerge)
   *
   * @remarks
   * Restores the original two edges and shared node from a merge snapshot.
   * This operation:
   * - Deletes the merged edge
   * - Recreates the shared node with original ID
   * - Recreates both original edges with original IDs
   * - Restores associations to their original edges
   *
   * All operations are atomic (single transaction).
   *
   * @param projectId - UUID del proyecto
   * @param mergedEdgeId - ID of the merged edge to undo
   * @param snapshot - Snapshot from the merge operation
   * @returns Restored edges and node
   *
   * @example
   * ```typescript
   * // After a merge
   * const mergeResult = await canonicalNetworkApi.mergeEdges(projectId, edge1.id, edge2.id);
   *
   * // Later, to undo
   * const unmergeResult = await canonicalNetworkApi.unmergeEdge(
   *   projectId,
   *   mergeResult.mergedEdge.id,
   *   mergeResult.snapshot
   * );
   * console.log(`Restored edges: ${unmergeResult.restoredEdge1.id}, ${unmergeResult.restoredEdge2.id}`);
   * ```
   */
  async unmergeEdge(
    projectId: string,
    mergedEdgeId: string,
    snapshot: MergeSnapshot
  ): Promise<UnmergeEdgeResponse> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/network/edges/unmerge`;
    console.log('[canonicalNetworkApi] 📤 POST unmergeEdge (fetch):', {
      url,
      mergedEdgeId,
      hasToken: !!token,
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ mergedEdgeId, snapshot }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[canonicalNetworkApi] ❌ unmergeEdge error:', {
          status: response.status,
          data: errorData,
        });

        const error = new Error(`HTTP ${response.status}`) as any;
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      const result = await response.json();
      console.log('[canonicalNetworkApi] ✅ unmergeEdge response:', {
        restoredEdge1Id: result.restoredEdge1.id,
        restoredEdge2Id: result.restoredEdge2.id,
        restoredNodeId: result.restoredNode.id,
      });
      return result;
    } catch (error: any) {
      console.error('[canonicalNetworkApi] ❌ unmergeEdge error:', {
        message: error.message,
        status: error.status,
      });
      throw error;
    }
  },

  // ==================== MULTI-MERGE V1 ====================

  /**
   * Validate if edges form a valid chain for multi-merge
   *
   * @remarks
   * This endpoint validates whether a set of N edges (N ≥ 2) forms a valid linear
   * chain that can be merged into a single edge. Used for real-time validation
   * during edge selection with Shift+Click.
   *
   * **Validation rules:**
   * - All edges must belong to the same project
   * - All edges must have status = 'active'
   * - Edges must form a linear chain (graph-connected with exactly 2 extremes)
   * - Intermediate nodes must have degree = 2 (only connect to the chain)
   * - Critical attributes must match: roadClass, oneway, layer
   * - If oneway, edges must form sequential flow (e1→n2→e2→n3→e3)
   * - No edges can have blocking external references (WasteRouteSegment)
   *
   * @param projectId - UUID del proyecto
   * @param edgeIds - Array of edge IDs to validate (min 2)
   * @returns Validation result with isValid, errorMessage, and orderedEdgeIds
   *
   * @example
   * ```typescript
   * const result = await canonicalNetworkApi.validateChain(projectId, [edge1.id, edge2.id, edge3.id]);
   * if (result.isValid) {
   *   console.log('Valid chain! Ordered IDs:', result.orderedEdgeIds);
   * } else {
   *   console.error('Invalid chain:', result.errorMessage);
   * }
   * ```
   */
  async validateChain(
    projectId: string,
    edgeIds: string[]
  ): Promise<ChainValidationResult> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/network/edges/validate-chain`;
    console.log('[canonicalNetworkApi] 📤 POST validateChain:', {
      url,
      edgeCount: edgeIds.length,
      hasToken: !!token,
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ edgeIds }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[canonicalNetworkApi] ❌ validateChain error:', {
          status: response.status,
          data: errorData,
        });

        const error = new Error(`HTTP ${response.status}`) as any;
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      const result = await response.json();
      console.log('[canonicalNetworkApi] ✅ validateChain response:', {
        isValid: result.isValid,
        errorMessage: result.errorMessage,
        orderedEdgeCount: result.orderedEdgeIds?.length,
      });
      return result;
    } catch (error: any) {
      console.error('[canonicalNetworkApi] ❌ validateChain error:', {
        message: error.message,
        status: error.status,
      });
      throw error;
    }
  },

  /**
   * Merge N edges that form a linear chain into a single edge (Multi-Merge V1)
   *
   * @remarks
   * This operation merges N edges (N ≥ 2) that form a linear chain into a single edge.
   * All intermediate nodes between the edges are removed and become shape points in
   * the merged geometry.
   *
   * **What happens:**
   * - Creates new edge from external nodes of first and last edges
   * - Intermediate node coordinates become internal shape points
   * - NetworkPointAssociations migrated to new edge
   * - Original edges and intermediate nodes marked as deprecated
   * - Returns complete snapshot for undo capability
   *
   * All operations are atomic (single transaction).
   *
   * @param projectId - UUID del proyecto
   * @param edgeIds - Array of edge IDs to merge (any order, will be topologically sorted)
   * @returns Merged edge data and complete snapshot for undo
   *
   * @example
   * ```typescript
   * const result = await canonicalNetworkApi.mergeMultipleEdges(projectId, [edge1.id, edge2.id, edge3.id]);
   * console.log(`Merged edge created: ${result.mergedEdge.id}`);
   * console.log(`Deleted ${result.deletedEdgeIds.length} edges and ${result.deletedNodeIds.length} nodes`);
   * // Save snapshot for undo
   * const snapshot = result.snapshot;
   * ```
   */
  async mergeMultipleEdges(
    projectId: string,
    edgeIds: string[]
  ): Promise<MergeMultipleEdgesResponse> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/network/edges/merge-multiple`;
    console.log('[canonicalNetworkApi] 📤 POST mergeMultipleEdges:', {
      url,
      edgeCount: edgeIds.length,
      hasToken: !!token,
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ edgeIds }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[canonicalNetworkApi] ❌ mergeMultipleEdges error:', {
          status: response.status,
          data: errorData,
        });

        const error = new Error(`HTTP ${response.status}`) as any;
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      const result = await response.json();
      console.log('[canonicalNetworkApi] ✅ mergeMultipleEdges response:', {
        mergedEdgeId: result.mergedEdge.id,
        deletedEdgeCount: result.deletedEdgeIds.length,
        deletedNodeCount: result.deletedNodeIds.length,
        migratedAssociationCount: result.migratedAssociationIds.length,
      });
      return result;
    } catch (error: any) {
      console.error('[canonicalNetworkApi] ❌ mergeMultipleEdges error:', {
        message: error.message,
        status: error.status,
      });
      throw error;
    }
  },

  /**
   * Undo a multi-merge operation (unmerge N edges)
   *
   * @remarks
   * This is the inverse of mergeMultipleEdges operation, used for undo functionality.
   * Restores all original edges and intermediate nodes from a snapshot with their
   * original IDs.
   *
   * **What happens:**
   * - Deletes the merged edge
   * - Recreates all intermediate nodes with original IDs
   * - Recreates all original edges with original IDs
   * - Redistributes associations back to original edges
   *
   * All operations are atomic (single transaction).
   *
   * @param projectId - UUID del proyecto
   * @param mergedEdgeId - ID of the merged edge to undo
   * @param snapshot - Snapshot from the merge operation
   * @returns Restored edges and nodes
   *
   * @example
   * ```typescript
   * // After a multi-merge
   * const mergeResult = await canonicalNetworkApi.mergeMultipleEdges(projectId, edgeIds);
   *
   * // Later, to undo
   * const unmergeResult = await canonicalNetworkApi.unmergeMultipleEdges(
   *   projectId,
   *   mergeResult.mergedEdge.id,
   *   mergeResult.snapshot
   * );
   * console.log(`Restored ${unmergeResult.restoredEdges.length} edges`);
   * console.log(`Restored ${unmergeResult.restoredNodes.length} nodes`);
   * ```
   */
  async unmergeMultipleEdges(
    projectId: string,
    mergedEdgeId: string,
    snapshot: MultiMergeSnapshot
  ): Promise<UnmergeMultipleEdgesResponse> {
    const token = getAuthToken();
    const url = `${API_URL}/projects/${projectId}/network/edges/unmerge-multiple`;
    console.log('[canonicalNetworkApi] 📤 POST unmergeMultipleEdges:', {
      url,
      mergedEdgeId,
      originalEdgeCount: snapshot.originalEdges.length,
      hasToken: !!token,
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ mergedEdgeId, snapshot }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[canonicalNetworkApi] ❌ unmergeMultipleEdges error:', {
          status: response.status,
          data: errorData,
        });

        const error = new Error(`HTTP ${response.status}`) as any;
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      const result = await response.json();
      console.log('[canonicalNetworkApi] ✅ unmergeMultipleEdges response:', {
        restoredEdgeCount: result.restoredEdges.length,
        restoredNodeCount: result.restoredNodes.length,
      });
      return result;
    } catch (error: any) {
      console.error('[canonicalNetworkApi] ❌ unmergeMultipleEdges error:', {
        message: error.message,
        status: error.status,
      });
      throw error;
    }
  },

};
